import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { getActeurById, updateActeur } from "../../services/acteursServices";
import { toast } from "react-toastify";

const emptyActeur = {
  nom: "",
  prenom: "",
  email: "",
  tel: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "",
  date_naissance: "",
  photo_url: "",
  club_acteur: "",
  licence_number: "",
};

const normalizeDate = (value) => {
  if (!value) return "";
  try {
    return String(value).slice(0, 10); // "YYYY-MM-DD"
  } catch {
    return "";
  }
};

const EditActeurModal = ({ isOpen, onClose, acteurId, refreshList }) => {
  const [acteurData, setActeurData] = useState(emptyActeur);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Chargement des données de l'acteur
  useEffect(() => {
    if (!isOpen || !acteurId) return;

    const fetchActeur = async () => {
      setIsLoading(true);
      try {
        const data = await getActeurById(acteurId);
        setActeurData({
          ...emptyActeur,
          ...data,
          date_naissance: normalizeDate(data?.date_naissance),
        });
      } catch (error) {
        console.error("Erreur lors de la récupération de l'acteur:", error);
        toast.error("Erreur lors de la récupération de l'acteur");
      } finally {
        setIsLoading(false);
      }
    };

    fetchActeur();
  }, [isOpen, acteurId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setActeurData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetState = () => {
    setActeurData(emptyActeur);
    setIsLoading(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !acteurId) return;

    setIsSubmitting(true);

    const finalActeurData = {
      ...acteurData,
      photo_url: acteurData.photo_url || "/img/user_avatar.jpg",
    };

    try {
      await updateActeur(acteurId, finalActeurData);
      toast.success("Acteur mis à jour avec succès");
      await refreshList();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'acteur:", error);
      toast.error("Erreur lors de la mise à jour de l'acteur");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-2">
      <div className="modal-box bg-white dark:bg-slate-900 p-0 rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base sm:text-lg font-bold uppercase tracking-[0.18em] text-white">
              Modifier un acteur
            </h3>
            <p className="mt-1 text-xs text-white/85">
              Mettez à jour les informations d’un arbitre, commissaire ou autre acteur du judo.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30"
          >
            <FaTimes />
          </button>
        </div>

        {/* CONTENU */}
        {isLoading ? (
          <div className="px-6 py-8 text-center text-sm text-slate-600 dark:text-slate-200">
            Chargement des informations de l’acteur...
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4 sm:px-6 sm:py-5 space-y-5">
            {/* Identité */}
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-3">
                Identité
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="nom"
                    value={acteurData.nom}
                    onChange={handleChange}
                    required
                    placeholder="DUPONT"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="prenom"
                    value={acteurData.prenom}
                    onChange={handleChange}
                    required
                    placeholder="Jean"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Genre <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="select select-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    name="genre"
                    value={acteurData.genre}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionner le genre</option>
                    <option value="Masculin">Masculin</option>
                    <option value="Féminin">Féminin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Date de naissance{" "}
                    <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="date"
                    name="date_naissance"
                    value={acteurData.date_naissance || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* Coordonnées */}
            <section className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-3">
                Coordonnées
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Email <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="email"
                    name="email"
                    value={acteurData.email || ""}
                    onChange={handleChange}
                    placeholder="exemple@mail.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Téléphone <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="tel"
                    name="tel"
                    value={acteurData.tel || ""}
                    onChange={handleChange}
                    placeholder="06 00 00 00 00"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1">
                    Adresse <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="adresse"
                    value={acteurData.adresse || ""}
                    onChange={handleChange}
                    placeholder="12 rue du Dojo"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Code postal <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="cp"
                    value={acteurData.cp || ""}
                    onChange={handleChange}
                    placeholder="33000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Commune <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="commune"
                    value={acteurData.commune || ""}
                    onChange={handleChange}
                    placeholder="Bordeaux"
                  />
                </div>
              </div>
            </section>

            {/* Infos Judo */}
            <section className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-3">
                Informations Judo
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Club de l'acteur{" "}
                    <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="club_acteur"
                    value={acteurData.club_acteur || ""}
                    onChange={handleChange}
                    placeholder="US Bouscat Judo"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Numéro de licence{" "}
                    <span className="text-[0.65rem] text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="text"
                    name="licence_number"
                    value={acteurData.licence_number || ""}
                    onChange={handleChange}
                    placeholder="XXXXXXXXX"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1">
                    URL de la photo{" "}
                    <span className="text-[0.65rem] text-slate-400">
                      (avatar par défaut si vide)
                    </span>
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    type="url"
                    name="photo_url"
                    value={acteurData.photo_url || ""}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </section>

            {/* ACTIONS */}
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
              <Button
                text="Annuler"
                icon={FaTimes}
                type="button"
                onClick={handleClose}
                className="btn-ghost gap-2 !px-4 !py-2"
              />
              <Button
                text={isSubmitting ? "Enregistrement..." : "Mettre à jour"}
                icon={FaSave}
                type="submit"
                className="btn-primary gap-2 !px-4 !py-2"
                disabled={isSubmitting}
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditActeurModal;
